import { motion } from 'motion/react';
import { Toaster } from './ui/sonner';

export function ContractorListSkeleton() {
  return (
    <div className="app-loading-state grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" role="status" aria-label="Loading contractors">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="p-4 border rounded-lg bg-white">
          <div className="flex items-start gap-4">
            {/* Avatar skeleton */}
            <div className="relative h-12 w-12 rounded-full bg-gray-200 overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </div>
            
            <div className="flex-1 min-w-0 space-y-3">
              {/* Name and company */}
              <div className="space-y-2">
                <div className="relative h-5 w-32 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                </div>
                <div className="relative h-4 w-24 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: 0.1
                    }}
                  />
                </div>
              </div>

              {/* Contact info */}
              <div className="flex items-center gap-4">
                <div className="relative h-4 w-40 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: 0.2
                    }}
                  />
                </div>
                <div className="relative h-4 w-28 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: 0.3
                    }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4">
                <div className="relative h-4 w-12 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: 0.4
                    }}
                  />
                </div>
                <div className="relative h-4 w-20 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: 0.5
                    }}
                  />
                </div>
                <div className="relative h-4 w-24 bg-gray-200 rounded overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: 0.6
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ContractorDetailsSkeleton() {
  return (
    <div className="app-loading-state space-y-6" role="status" aria-label="Loading contractor details">
      {/* Header skeleton */}
      <div className="flex items-start gap-4">
        <div className="relative h-16 w-16 rounded-full bg-gray-200 overflow-hidden">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
            animate={{
              x: ['-100%', '100%']
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>
        <div className="space-y-2 flex-1">
          <div className="relative h-7 w-48 bg-gray-200 rounded overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              animate={{
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </div>
          <div className="relative h-5 w-36 bg-gray-200 rounded overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              animate={{
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear",
                delay: 0.1
              }}
            />
          </div>
          <div className="relative h-4 w-64 bg-gray-200 rounded overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              animate={{
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear",
                delay: 0.2
              }}
            />
          </div>
        </div>
      </div>

      {/* Stats cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-6 border rounded-lg bg-white">
            <div className="space-y-3">
              <div className="relative h-4 w-24 bg-gray-200 rounded overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                  animate={{
                    x: ['-100%', '100%']
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.1
                  }}
                />
              </div>
              <div className="relative h-8 w-16 bg-gray-200 rounded overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                  animate={{
                    x: ['-100%', '100%']
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.1 + 0.1
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs skeleton */}
      <div className="space-y-4">
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="relative h-10 w-24 bg-gray-200 rounded overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear",
                  delay: i * 0.05
                }}
              />
            </div>
          ))}
        </div>

        {/* Content skeleton */}
        <div className="border rounded-lg p-6 bg-white space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="relative h-5 w-3/4 bg-gray-200 rounded overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                  animate={{
                    x: ['-100%', '100%']
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.1
                  }}
                />
              </div>
              <div className="relative h-4 w-full bg-gray-200 rounded overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                  animate={{
                    x: ['-100%', '100%']
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.1 + 0.1
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
<Toaster  position={'top-right'}/>

    </div>
  );
}

export function TeamManagementSkeleton() {
  return (
    <div className="app-loading-state space-y-6 tailwind-prefix" role="status" aria-label="Loading team members">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative h-10 w-full max-w-md overflow-hidden rounded-lg bg-gray-200">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <div className="relative h-10 w-36 overflow-hidden rounded-lg bg-gray-200">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.1 }}
            />
          </div>
        </div>

        <div className="relative h-10 w-32 overflow-hidden rounded-lg bg-gray-200">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.2 }}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        <div className="grid grid-cols-5 gap-4 border-b border-border bg-gray-100 px-6 py-4">
          {["Name", "Email", "Role", "Permissions", "Status"].map((heading, index) => (
            <div key={heading} className="relative h-4 overflow-hidden rounded bg-gray-200">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear",
                  delay: index * 0.1,
                }}
              />
            </div>
          ))}
        </div>

        <div className="divide-y divide-border">
          {[1, 2, 3, 4, 5].map((row) => (
            <div key={row} className="grid grid-cols-6 gap-4 px-6 py-5">
              <div className="col-span-2 flex items-center gap-3">
                <div className="relative h-10 w-10 overflow-hidden rounded-full bg-gray-200">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  />
                </div>
                <div className="space-y-2">
                  <div className="relative h-4 w-32 overflow-hidden rounded bg-gray-200">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.1 }}
                    />
                  </div>
                  <div className="relative h-3 w-24 overflow-hidden rounded bg-gray-200">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.2 }}
                    />
                  </div>
                </div>
              </div>

              {[1, 2, 3].map((cell) => (
                <div key={cell} className="relative h-4 overflow-hidden rounded bg-gray-200 self-center">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                      delay: cell * 0.1,
                    }}
                  />
                </div>
              ))}

              <div className="flex items-center justify-between gap-3">
                <div className="relative h-6 w-12 overflow-hidden rounded-full bg-gray-200">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.3 }}
                  />
                </div>
                <div className="relative h-8 w-20 overflow-hidden rounded-lg bg-gray-200">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.4 }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export function AdminLeadsShimmer() {
  return (
    <div className="app-loading-state w-full px-6 py-6" role="status" aria-label="Loading leads">
      {/* Stats Shimmer */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 shadow-sm p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <Shimmer className="h-4 w-24" />
              <Shimmer className="h-9 w-9 rounded-lg" />
            </div>
            <Shimmer className="h-7 w-16 mb-2" />
            <Shimmer className="h-3 w-20" />
          </div>
        ))}
      </div>

      {/* Filters Shimmer */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shimmer className="h-8 w-8 rounded-lg" />
            <Shimmer className="h-4 w-16" />
          </div>

          <div className="flex items-center gap-2">
            <Shimmer className="h-3 w-20" />
            <Shimmer className="h-6 w-14 rounded-md" />
            <Shimmer className="h-6 w-16 rounded-md" />
            <Shimmer className="h-6 w-16 rounded-md" />
            <Shimmer className="h-6 w-24 rounded-md" />
            <Shimmer className="h-6 w-16 rounded-md" />
          </div>
        </div>

        {/* Basic filters row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Shimmer className="h-10 w-full rounded-lg" />
          <Shimmer className="h-10 w-full rounded-lg" />
          <Shimmer className="h-10 w-full rounded-lg" />
        </div>

        {/* Advanced filters */}
        <div className="pt-4 border-t border-gray-200">
          <Shimmer className="h-3 w-28 mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Shimmer className="h-3 w-16 mb-2" />
              <Shimmer className="h-10 w-full rounded-lg" />
            </div>
            <div>
              <Shimmer className="h-3 w-16 mb-2" />
              <Shimmer className="h-10 w-full rounded-lg" />
            </div>
            <div>
              <Shimmer className="h-3 w-20 mb-2" />
              <Shimmer className="h-10 w-full rounded-lg" />
            </div>
            <div>
              <Shimmer className="h-3 w-20 mb-2" />
              <Shimmer className="h-10 w-full rounded-lg" />
            </div>
            <div className="lg:col-span-1">
              <Shimmer className="h-3 w-16 mb-2" />
              <Shimmer className="h-10 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Results count shimmer */}
      <div className="mb-4 flex items-center justify-between">
        <Shimmer className="h-4 w-48" />
        <div className="flex items-center gap-2">
          <Shimmer className="h-4 w-12" />
          <Shimmer className="h-8 w-32 rounded-lg" />
        </div>
      </div>

      {/* Table Shimmer */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-8 gap-4 px-4 py-3 border-b border-gray-200">
          <Shimmer className="h-3 w-10" />
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-3 w-28" />
          <Shimmer className="h-3 w-20" />
          <Shimmer className="h-3 w-16" />
          <Shimmer className="h-3 w-16" />
        </div>

        {/* Table rows */}
        {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
          <div
            key={row}
            className="grid grid-cols-8 gap-4 px-4 py-4 border-b border-gray-100"
          >
            <Shimmer className="h-4 w-10" />
            <div className="flex flex-col gap-2">
              <Shimmer className="h-4 w-24" />
              <Shimmer className="h-3 w-16" />
            </div>
            <div className="flex flex-col gap-2">
              <Shimmer className="h-4 w-28" />
              <Shimmer className="h-3 w-20" />
            </div>
            <Shimmer className="h-4 w-24" />
            <div className="flex flex-col gap-2">
              <Shimmer className="h-4 w-20" />
              <Shimmer className="h-3 w-24" />
            </div>
            <Shimmer className="h-4 w-20" />
            <Shimmer className="h-6 w-16 rounded-full" />
            <div className="flex gap-2 justify-end">
              <Shimmer className="h-7 w-7 rounded-md" />
              <Shimmer className="h-7 w-7 rounded-md" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div className={`app-skeleton relative overflow-hidden rounded ${className}`} aria-hidden="true">
      <div className="app-skeleton__wave absolute inset-0 -translate-x-full animate-shimmer" />
    </div>
  );
}

export function HomeownerRowShimmer() {
  return (
    <div className="app-loading-state bg-white rounded-lg border border-gray-200 p-4" role="status" aria-label="Loading homeowner">
      <div className="flex items-center justify-between gap-4">

        {/* Left: Avatar + Name */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Shimmer className="w-12 h-12 rounded-full" />

          <div className="space-y-2 w-[180px]">
            <Shimmer className="h-4 w-[140px]" />
            <Shimmer className="h-3 w-[100px]" />
            <Shimmer className="h-3 w-[120px]" />
          </div>
        </div>

        {/* Email */}
        <div className="hidden md:flex items-center gap-2 flex-1">
          <Shimmer className="w-4 h-4 rounded" />
          <Shimmer className="h-4 w-[200px]" />
        </div>

        {/* Location */}
        <div className="hidden lg:flex items-center gap-2 flex-1">
          <Shimmer className="w-4 h-4 rounded" />
          <Shimmer className="h-4 w-[160px]" />
        </div>

        {/* Active Leads Box */}
        <div className="hidden sm:flex flex-col items-center px-3 py-2 border border-gray-200 rounded-md min-w-[90px]">
          <Shimmer className="h-3 w-[70px] mb-1" />
          <Shimmer className="h-4 w-[30px]" />
        </div>

        {/* Status Badge */}
        <Shimmer className="h-6 w-[70px] rounded-full" />

      </div>
    </div>
  );
}

export  function JobDetailsShimmer() {
  return (
    <div className="app-loading-state space-y-6" role="status" aria-label="Loading job details">

      {/* Top Section */}
      <div className="grid grid-cols-12 gap-6">

        {/* Image */}
        <div className="col-span-9">
          <Shimmer className="w-full h-[420px] rounded-lg" />
        </div>

        {/* Right Panel */}
        <div className="col-span-3 space-y-4">

          <div className="border rounded-lg p-4 space-y-3">
            <Shimmer className="h-5 w-24" />
            <Shimmer className="h-4 w-32" />
            <Shimmer className="h-6 w-20" />
            <Shimmer className="h-4 w-full" />
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <Shimmer className="h-5 w-16" />
            <Shimmer className="h-4 w-12" />
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <Shimmer className="h-10 w-full rounded-md" />
            <Shimmer className="h-10 w-full rounded-md" />
          </div>

        </div>
      </div>

      {/* Description */}
      <div className="border rounded-lg p-4 space-y-3">
        <Shimmer className="h-5 w-32" />
        <Shimmer className="h-4 w-full" />
        <Shimmer className="h-4 w-3/4" />
      </div>

      {/* Assignment Status */}
      <div className="border rounded-lg p-4 space-y-4">
        <Shimmer className="h-5 w-40" />

        <div className="flex items-center gap-4">
          <Shimmer className="h-10 w-10 rounded-full" />
          <div className="space-y-2 flex-1">
            <Shimmer className="h-4 w-40" />
            <Shimmer className="h-3 w-52" />
          </div>
          <Shimmer className="h-5 w-20" />
        </div>

        <Shimmer className="h-10 w-full rounded-md" />
      </div>

      {/* History */}
      <div className="border rounded-lg p-4 space-y-4">
        <Shimmer className="h-5 w-24" />

        {[1,2].map((i) => (
          <div key={i} className="flex justify-between">
            <div className="space-y-2">
              <Shimmer className="h-4 w-40" />
              <Shimmer className="h-3 w-32" />
            </div>
            <Shimmer className="h-3 w-24" />
          </div>
        ))}
      </div>

    </div>
  );
}
export default function ProjectCardShimmer() {
  return (
    <div className="app-loading-state bg-white rounded-xl shadow-sm border overflow-hidden p-4 space-y-4" role="status" aria-label="Loading project">

      {/* Image */}
      <Shimmer className="w-full h-48 rounded-lg" />

      {/* Title */}
      <Shimmer className="h-5 w-1/3" />

      {/* Description */}
      <Shimmer className="h-4 w-1/2" />

      {/* Address */}
      <div className="flex items-center gap-2">
        <Shimmer className="h-4 w-4 rounded-full" />
        <Shimmer className="h-4 w-2/3" />
      </div>

      {/* Price */}
      <div className="flex items-center gap-2">
        <Shimmer className="h-4 w-4 rounded-full" />
        <Shimmer className="h-4 w-20" />
      </div>

      {/* Date */}
      <div className="flex items-center gap-2">
        <Shimmer className="h-4 w-4 rounded-full" />
        <Shimmer className="h-4 w-32" />
      </div>

      {/* Tag */}
      <Shimmer className="h-6 w-32 rounded-full" />

      {/* Footer */}
      <div className="flex justify-between items-center pt-2">
        <Shimmer className="h-4 w-40" />
        <Shimmer className="h-4 w-6" />
      </div>

    </div>
  );
}
export function HomeownerInfoShimmer() {
  return (
    <div className="app-loading-state bg-white border rounded-xl p-6 space-y-6" role="status" aria-label="Loading homeowner information">
      
      {/* Title */}
      <div className="space-y-2">
        <Shimmer className="h-5 w-48 rounded-md" />
        <Shimmer className="h-4 w-72 rounded-md" />
      </div>

      {/* Avatar + Name */}
      <div className="flex items-center gap-4">
        <Shimmer className="h-16 w-16 rounded-full" />
        <div className="space-y-2">
          <Shimmer className="h-5 w-40 rounded-md" />
          <Shimmer className="h-4 w-32 rounded-md" />
        </div>
      </div>

      {/* Email + Phone */}
      <div className="grid grid-cols-2 gap-6">
        <div className="flex items-start gap-3">
          <Shimmer className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Shimmer className="h-4 w-20 rounded-md" />
            <Shimmer className="h-4 w-40 rounded-md" />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Shimmer className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Shimmer className="h-4 w-16 rounded-md" />
            <Shimmer className="h-4 w-28 rounded-md" />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="flex items-start gap-3">
        <Shimmer className="h-10 w-10 rounded-lg" />
        <div className="space-y-2 w-full">
          <Shimmer className="h-4 w-20 rounded-md" />
          <Shimmer className="h-4 w-full rounded-md" />
        </div>
      </div>

      {/* Button */}
      <Shimmer className="h-12 w-full rounded-lg" />
    </div>
  );
}
export function SummaryCardsSkeleton() {
  return (
    <div className="app-loading-state grid grid-cols-1 md:grid-cols-3 gap-5" role="status" aria-label="Loading dashboard summary">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-4">
            <Shimmer className="w-12 h-12 rounded-lg" />
            <div className="flex-1">
              <Shimmer className="h-7 w-16 mb-2 rounded" />
              <Shimmer className="h-4 w-32 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ActionRequiredSkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-8 shadow-sm" role="status" aria-label="Loading required actions">
      <div className="flex items-center gap-3 mb-7">
        <Shimmer className="w-12 h-12 rounded-xl" />
        <div className="flex-1">
          <Shimmer className="h-6 w-48 mb-2 rounded" />
          <Shimmer className="h-4 w-64 rounded" />
        </div>
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-gray-50 rounded-r-xl p-6 border-l-4 border-gray-300">
            <div className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-4 flex-1">
                <Shimmer className="w-6 h-6 rounded" />
                <div className="flex-1">
                  <Shimmer className="h-5 w-3/4 mb-2 rounded" />
                  <Shimmer className="h-4 w-1/2 rounded" />
                </div>
              </div>
              <Shimmer className="h-12 w-32 rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ActiveProjectsSkeleton() {
  return (
    <div className="app-loading-state" role="status" aria-label="Loading active projects">
      <div className="mb-6">
        <Shimmer className="h-6 w-48 mb-2 rounded" />
        <Shimmer className="h-4 w-36 rounded" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white border-2 border-gray-200 rounded-xl p-6">
            <div className="flex justify-between items-start mb-5">
              <div className="flex-1">
                <Shimmer className="h-6 w-3/4 mb-4 rounded" />
                <div className="space-y-3">
                  <Shimmer className="h-4 w-full rounded" />
                  <Shimmer className="h-4 w-2/3 rounded" />
                  <Shimmer className="h-4 w-1/2 rounded" />
                </div>
              </div>
              <Shimmer className="w-20 h-7 rounded-xl" />
            </div>
            <div className="pt-4 border-t border-gray-100">
              <Shimmer className="h-12 w-full rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContractsOverviewSkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-6 shadow-sm" role="status" aria-label="Loading contracts overview">
      <Shimmer className="h-6 w-32 mb-5 rounded" />
      <div className="bg-gray-50 p-1 rounded-xl border border-gray-200 mb-6">
        <div className="flex gap-1">
          {[1, 2, 3].map((i) => (
            <Shimmer key={i} className="h-11 flex-1 rounded-lg" />
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <Shimmer key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

export function ProfileCompletionSkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-6 shadow-sm" role="status" aria-label="Loading profile completion">
      <Shimmer className="h-6 w-48 mb-5 rounded" />
      <div className="mb-6">
        <Shimmer className="h-4 w-full mb-2.5 rounded" />
        <Shimmer className="h-2.5 w-full rounded-full" />
      </div>
      <div className="space-y-3.5 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Shimmer key={i} className="h-5 w-full rounded" />
        ))}
      </div>
      <Shimmer className="h-11 w-full rounded-xl" />
    </div>
  );
}

export function RecentActivitySkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-6 shadow-sm" role="status" aria-label="Loading recent activity">
      <Shimmer className="h-6 w-36 mb-5 rounded" />
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-start gap-3.5 pb-4 border-b border-gray-100 last:border-0">
            <Shimmer className="w-10 h-10 rounded-lg" />
            <div className="flex-1">
              <Shimmer className="h-4 w-full mb-2 rounded" />
              <Shimmer className="h-3 w-20 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MessagesSkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-6 shadow-sm" role="status" aria-label="Loading notifications">
      <div className="flex items-center justify-between mb-5">
        <Shimmer className="h-6 w-24 rounded" />
        <Shimmer className="h-6 w-8 rounded-full" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 rounded-xl border-2 border-gray-200">
            <div className="flex items-start gap-3">
              <Shimmer className="w-10 h-10 rounded-full" />
              <div className="flex-1">
                <Shimmer className="h-4 w-32 mb-2 rounded" />
                <Shimmer className="h-3 w-full rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export function IncomingLeadsSkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-6 shadow-sm" role="status" aria-label="Loading incoming leads">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1">
          <Shimmer className="h-6 w-36 mb-2 rounded" />
          <Shimmer className="h-4 w-40 rounded" />
        </div>
        <Shimmer className="w-20 h-8 rounded-lg" />
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <Shimmer className="h-5 w-32 mb-2 rounded" />
                <Shimmer className="h-4 w-40 rounded" />
              </div>
              <Shimmer className="h-4 w-20 rounded" />
            </div>
            <Shimmer className="h-12 w-full mb-4 rounded" />
            <div className="flex gap-4 mb-4 pb-4 border-b border-gray-100">
              <Shimmer className="h-4 w-28 rounded" />
              <Shimmer className="h-4 w-32 rounded" />
            </div>
            <div className="flex gap-3">
              <Shimmer className="h-10 flex-1 rounded-lg" />
              <Shimmer className="h-10 w-24 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AcceptedLeadsSkeleton() {
  return (
    <div className="app-loading-state bg-white border border-gray-200 rounded-xl p-6 shadow-sm" role="status" aria-label="Loading accepted leads">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1">
          <Shimmer className="h-6 w-36 mb-2 rounded" />
          <Shimmer className="h-4 w-40 rounded" />
        </div>
        <Shimmer className="h-5 w-16 rounded" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <Shimmer className="h-5 w-40 mb-2 rounded" />
                <Shimmer className="h-4 w-36 rounded" />
              </div>
              <Shimmer className="h-6 w-24 rounded-md" />
            </div>
            <div className="flex gap-4 mb-3">
              <Shimmer className="h-4 w-24 rounded" />
              <Shimmer className="h-4 w-20 rounded" />
              <Shimmer className="h-4 w-20 rounded" />
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <Shimmer className="h-4 w-48 rounded" />
              <Shimmer className="h-4 w-20 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
